import * as XLSX from 'xlsx';
import type { ImportAccountRow } from '../../shared/importAccountTypes';
import { parseImportUserName } from './mapUsernameToParty';

/** Remove all `=` and `"` characters (Excel formula/export artifacts), then trim. */
function sanitizeCellValue(value: unknown): string {
  return String(value ?? '')
    .replace(/[="]/g, '')
    .trim();
}

function normalizeHeader(value: unknown): string {
  return sanitizeCellValue(value)
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function parseNumber(value: unknown): number {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const cleaned = sanitizeCellValue(value).replace(/,/g, '');
  if (!cleaned) return 0;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function findColumnIndex(headers: string[], candidates: string[]): number {
  for (const candidate of candidates) {
    const idx = headers.findIndex((h) => h === candidate || h.includes(candidate));
    if (idx >= 0) return idx;
  }
  return -1;
}

function sheetRowsToImportRows(matrix: unknown[][]): ImportAccountRow[] {
  if (matrix.length < 2) return [];

  const headerRow = matrix[0].map(normalizeHeader);
  const userNameIdx = findColumnIndex(headerRow, ['user name', 'username']);
  const crIdx = findColumnIndex(headerRow, ['cr']);
  const drIdx = findColumnIndex(headerRow, ['dr']);
  const clientPlIdx = findColumnIndex(headerRow, ['client(p/l)', 'client p/l', 'client pl', 'client']);
  const exposureIdx = findColumnIndex(headerRow, ['exposure']);
  const availableIdx = findColumnIndex(headerRow, ['available']);
  const accountTypeIdx = findColumnIndex(headerRow, ['account type', 'accounttype']);
  const percentageIdx = findColumnIndex(headerRow, ['percentage', '%']);

  if (userNameIdx < 0) {
    throw new Error('Missing required column: User Name');
  }

  // Unnamed column between CR and Client(P/L) is treated as DR when no DR header exists.
  const inferredDrIdx =
    drIdx >= 0
      ? drIdx
      : crIdx >= 0 && clientPlIdx > crIdx + 1
        ? crIdx + 1
        : -1;

  const rows: ImportAccountRow[] = [];

  for (let i = 1; i < matrix.length; i++) {
    const row = matrix[i];
    if (!row || row.every((cell) => sanitizeCellValue(cell) === '')) continue;

    const userNameRaw = sanitizeCellValue(row[userNameIdx]);
    if (!userNameRaw) continue;

    const parsedName = parseImportUserName(userNameRaw);
    const filePercentage =
      percentageIdx >= 0 ? sanitizeCellValue(row[percentageIdx]) : '';

    rows.push({
      id: `row-${i}`,
      rowIndex: i,
      userNameRaw,
      loginId: parsedName.loginId,
      displayName: parsedName.displayName,
      cr: crIdx >= 0 ? parseNumber(row[crIdx]) : 0,
      dr: inferredDrIdx >= 0 ? parseNumber(row[inferredDrIdx]) : 0,
      clientPl: clientPlIdx >= 0 ? parseNumber(row[clientPlIdx]) : 0,
      exposure: exposureIdx >= 0 ? parseNumber(row[exposureIdx]) : 0,
      available: availableIdx >= 0 ? parseNumber(row[availableIdx]) : 0,
      accountType: accountTypeIdx >= 0 ? sanitizeCellValue(row[accountTypeIdx]) : '',
      percentage: filePercentage || '0',
      partyCodeInput: '',
      validatedRowPartyCode: '',
      status: 'pending',
    });
  }

  return rows;
}

function readMatrixFromText(text: string): unknown[][] {
  const delimiter = text.includes('\t') ? '\t' : ',';
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
  return lines.map((line) => {
    if (delimiter === '\t') {
      return line.split('\t').map((cell) => sanitizeCellValue(cell));
    }
    return line.split(',').map((cell) => sanitizeCellValue(cell));
  });
}

export async function parseImportAccountFile(file: File): Promise<ImportAccountRow[]> {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

  if (extension === 'csv' || extension === 'txt') {
    const text = await file.text();
    return sheetRowsToImportRows(readMatrixFromText(text));
  }

  if (extension === 'xlsx' || extension === 'xls') {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) return [];
    const sheet = workbook.Sheets[firstSheetName];
    const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' });
    return sheetRowsToImportRows(matrix);
  }

  throw new Error('Unsupported file type. Please upload .xlsx, .xls, or .csv');
}
